import { HexBoard } from '../game/hex/HexBoard';
import { MiniGameMode } from '../game/MiniGameMode';
import { RouteSearcherV4 } from '../game/RouteSearcherV4';
import { TileType } from '../game/hex/HexTypes';

export class MiniGameControls {
  private board: HexBoard;
  private miniGame: MiniGameMode;
  private routeSearcher: RouteSearcherV4;
  private timerInterval: number | null = null;
  
  constructor(board: HexBoard, miniGame: MiniGameMode) {
    this.board = board;
    this.miniGame = miniGame;
    this.routeSearcher = new RouteSearcherV4(board);
    this.initializeEventListeners();
    this.updateUI();
  }
  
  private initializeEventListeners(): void {
    // ゲーム開始ボタン
    const startBtn = document.getElementById('start-game-btn');
    if (startBtn) {
      startBtn.addEventListener('click', () => this.startGame());
    }
    
    // ラウンドリセットボタン
    const resetBtn = document.getElementById('reset-round-btn');
    if (resetBtn) {
      resetBtn.addEventListener('click', () => this.resetRound());
    }
    
    // 新しいゲームボタン
    const newGameBtn = document.getElementById('new-game-btn');
    if (newGameBtn) {
      newGameBtn.addEventListener('click', () => this.startNewGame());
    }
    
    // 正解表示ボタン
    const findRouteBtn = document.getElementById('find-route-btn');
    if (findRouteBtn) {
      findRouteBtn.addEventListener('click', () => this.findRoutes());
    }
    
    // 矢印を消すボタン
    const clearRouteBtn = document.getElementById('clear-route-btn');
    if (clearRouteBtn) {
      clearRouteBtn.addEventListener('click', () => this.clearRoutes());
    }
  }
  
  private startGame(): void {
    // 固定のタイル配置で開始（指定された配合、合計26個）
    this.board.regenerate({
      BLUE_1: 4,           // 青1
      RED_1: 4,            // 赤1
      BLUE_2: 4,           // 青2
      RED_2: 2,            // 赤2
      BLUE_1_RED_1: 6,     // 青1赤1
      BLUE_2_RED_1: 4,     // 青2赤1
      BLUE_1_RED_2: 2,     // 青1赤2
      BLUE_1_STAR: 0,
      RED_1_STAR: 0,
      BLUE_2_STAR: 0,
      RED_2_STAR: 0,
      BLUE_1_RED_1_STAR: 0,
      BLUE_2_RED_1_STAR: 0,
      BLUE_1_RED_2_STAR: 0
    });
    
    this.miniGame.start();
    this.startTimer();
    this.updateUI();
    
    // 描画を強制的に更新
    setTimeout(() => {
      (window as any).game.render();
    }, 50);
    
    // ボタンの表示切り替え
    const startBtn = document.getElementById('start-game-btn');
    const resetBtn = document.getElementById('reset-round-btn');
    if (startBtn) startBtn.style.display = 'none';
    if (resetBtn) resetBtn.style.display = 'block';
    
    // 履歴表示
    const history = document.getElementById('round-history');
    if (history) history.style.display = 'block';
  }
  
  private resetRound(): void {
    // 現在のラウンドをリセット（MiniGameMode内で処理）
    this.updateUI();
  }
  
  private startNewGame(): void {
    this.stopTimer();
    
    // ゲーム完了画面を隠す
    const completeDiv = document.getElementById('game-complete');
    if (completeDiv) completeDiv.style.display = 'none';
    
    // 開始ボタンを表示
    const startBtn = document.getElementById('start-game-btn');
    if (startBtn) startBtn.style.display = 'block';
    
    this.startGame();
  }
  
  private startTimer(): void {
    this.stopTimer();
    this.timerInterval = window.setInterval(() => {
      this.updateTimerDisplay();
      
      // ゲーム完了チェック
      if (this.miniGame.isGameComplete()) {
        this.handleGameComplete();
      }
    }, 100);
  }
  
  private stopTimer(): void {
    if (this.timerInterval !== null) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
  }
  
  private updateTimerDisplay(): void {
    const currentTimer = this.miniGame.getCurrentRoundTimer();
    const totalTime = this.miniGame.getTotalElapsedTime();
    
    // 現在のラウンド時間
    if (currentTimer) {
      const elapsed = currentTimer.endTime 
        ? currentTimer.elapsed 
        : Date.now() - currentTimer.startTime;
      const seconds = (elapsed / 1000).toFixed(1);
      const currentTimerEl = document.getElementById('current-timer');
      if (currentTimerEl) currentTimerEl.textContent = `${seconds}秒`;
    }
    
    // 合計時間（mm分ss.s秒形式）
    const totalSecondsFloat = totalTime / 1000;
    const minutes = Math.floor(totalSecondsFloat / 60);
    const seconds = (totalSecondsFloat % 60).toFixed(1);
    const totalTimerEl = document.getElementById('total-timer');
    if (totalTimerEl) {
      if (minutes > 0) {
        totalTimerEl.textContent = `${minutes}分${seconds}秒`;
      } else {
        totalTimerEl.textContent = `${seconds}秒`;
      }
    }
  }
  
  private handleGameComplete(): void {
    this.stopTimer();
    
    // 最終ラウンドの履歴を更新
    this.updateRoundHistory();
    
    // Canvas内でゲーム完了が表示されるため、HTML側の表示は不要
    // ゲーム完了はCanvas内で表示される
  }
  
  public updateUI(): void {
    if (!this.miniGame.isEnabled()) return;
    
    // ラウンド情報
    const currentRoundEl = document.getElementById('current-round');
    if (currentRoundEl) {
      currentRoundEl.textContent = this.miniGame.getCurrentRound().toString();
    }
    
    // ラウンド履歴を更新
    this.updateRoundHistory();
    
    // タイマー表示を更新
    this.updateTimerDisplay();
  }
  
  private updateRoundHistory(): void {
    const historyListEl = document.getElementById('round-history-list');
    if (!historyListEl) return;
    
    const roundStates = this.miniGame.getRoundStates();
    let html = '';
    
    roundStates.forEach(state => {
      const seconds = (state.timer.elapsed / 1000).toFixed(1);
      const statusClass = state.success ? 'success' : 'failure';
      const statusText = state.success ? '✓ 成功' : '✗ 失敗';
      
      html += `
        <div class="round-history-item">
          <span>ラウンド${state.round}</span>
          <span>${seconds}秒</span>
          <span class="${statusClass}">${statusText}</span>
        </div>
      `;
    });
    
    historyListEl.innerHTML = html;
  }
  
  private findRoutes(): void {
    this.routeSearcher.setMaxGreenChips(0);
    const routes = this.routeSearcher.findAllRoutes();
    this.displayRoutes(routes);
    // 矢印表示を有効化
    if (routes.length > 0 && routes[0].route) {
      (window as any).game.showRouteArrows(routes[0].route);
      // クリアボタンを表示
      const clearBtn = document.getElementById('clear-route-btn');
      if (clearBtn) clearBtn.style.display = 'block';
    }
  }
  
  private clearRoutes(): void {
    // 矢印を消す
    (window as any).game.showRouteArrows(null);
    // クリアボタンを非表示
    const clearBtn = document.getElementById('clear-route-btn');
    if (clearBtn) clearBtn.style.display = 'none';
    // ルート表示も消す
    const displayEl = document.getElementById('routes-display');
    if (displayEl) displayEl.style.display = 'none';
  }
  
  private displayRoutes(routes: any[]): void {
    const displayEl = document.getElementById('routes-display');
    const contentEl = document.getElementById('routes-content');
    
    if (!displayEl || !contentEl) return;
    
    if (routes.length === 0) {
      contentEl.innerHTML = '<p>ルートが見つかりませんでした</p>';
    } else {
      let html = `<p>見つかったルート数: ${routes.length}</p>`;
      routes.slice(0, 10).forEach((route, index) => {
        const tileCount = route.route ? route.route.length : 0;
        const pattern = route.marbles ? route.marbles.flatMap((m: any) => m.sequence).map((s: any) => s === 'RED' ? 'チム' : 'ドン') : [];
        html += `<div style="margin: 10px 0; padding: 10px; background: white; border-radius: 5px;">`;
        html += `<strong>ルート${index + 1}:</strong> ${tileCount}タイル<br>`;
        html += `<span style="font-size: 14px;">${pattern.join(' → ')}</span>`;
        html += `</div>`;
      });
      contentEl.innerHTML = html;
    }
    
    displayEl.style.display = 'block';
  }
  
}